import {Injectable, Renderer2, RendererFactory2} from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ColorSchemeService {

    private renderer: Renderer2;
    private colorScheme: "dark" | "light";
    // Define prefix for clearer and more readable class names in scss files
    private colorSchemePrefix = 'color-scheme-';
    private systemPreferenceListenerAttached = false;

    constructor(rendererFactory: RendererFactory2) {
        // Create new renderer from renderFactory, to make it possible to use renderer2 in a service
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    _detectPrefersColorScheme(): "dark" | "light" {
        // Detect if prefers-color-scheme is supported
        if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme)').media !== 'not all') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        // If the browser does not support prefers-color-scheme, default to dark.
        return 'dark';
    }

    _getColorScheme(): "dark" | "light" | null {
        const localStorageColorScheme = localStorage.getItem('prefers-color');
        if (localStorageColorScheme === 'dark' || localStorageColorScheme === 'light') {
            return localStorageColorScheme;
        }
        return null;
    }

    private applyScheme(scheme: "dark" | "light", persist: boolean) {
        this.colorScheme = scheme;
        if (persist) {
            localStorage.setItem('prefers-color', scheme);
        }
        // Remove the old color-scheme class
        this.renderer.removeClass(document.body, this.colorSchemePrefix + (scheme === 'dark' ? 'light' : 'dark'));
        // Add the new / current color-scheme class
        this.renderer.addClass(document.body, this.colorSchemePrefix + scheme);
    }

    private watchSystemPreference() {
        if (this.systemPreferenceListenerAttached || typeof window === 'undefined') {
            return;
        }
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event: MediaQueryListEvent) => {
            // Only follow the device while the user has not chosen a theme.
            if (this._getColorScheme()) {
                return;
            }
            this.applyScheme(event.matches ? 'dark' : 'light', false);
        };
        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', onChange);
        } else if (typeof media.addListener === 'function') {
            media.addListener(onChange);
        }
        this.systemPreferenceListenerAttached = true;
    }

    load() {
        const stored = this._getColorScheme();
        if (stored) {
            this.applyScheme(stored, false);
            return;
        }
        this.applyScheme(this._detectPrefersColorScheme(), false);
        this.watchSystemPreference();
    }

    update(scheme: "dark" | "light") {
        this.applyScheme(scheme, true);
    }

    toggleTheme() {
        const toUpdate = this.colorScheme === "dark"? "light" : "dark";
        this.update(toUpdate);
    }

    currentActive() {
        return this.colorScheme;
    }

}
